import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function MRangeSection() {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: 'ri-puzzle-line',
      title: 'Revolutionary Modular Design',
      description: 'Each component weighs under 55kg, enabling manual assembly in remote locations without heavy machinery'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Access Anywhere',
      description: 'Perfect for mountain peaks, forest clearings, and islands - places where traditional construction is impossible'
    },
    {
      icon: 'ri-timer-line',
      title: 'Rapid Assembly',
      description: '2-4 days assembly time with minimal tools, transforming logistics into a simple delivery operation'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Premium Quality',
      description: 'Galvanized steel frame, aluminium alloy panels, and 100mm polyurethane insulation for lasting durability'
    }
  ];

  const models = [
    {
      name: 'M5',
      dimensions: '7.56m × 3.6m × 3.2m',
      area: '26 m²',
      occupancy: '1-2 persons',
      modules: '18 modules',
      image: '/images/7f3649eeefc8.webp',
      description: 'Perfect balance of space and portability with stunning panoramic windows.'
    },
    {
      name: 'M7',
      dimensions: '10m × 3.6m × 3.2m',
      area: '36 m²',
      occupancy: '2-4 persons',
      modules: '24 modules',
      image: '/images/4f4d4413e7ef.webp',
      description: 'Extended living space with complete amenities for families.'
    },
    {
      name: 'M9',
      dimensions: '11.5m × 3.3m × 3.2m',
      area: '38 m²',
      occupancy: '2-4 persons',
      modules: '28 modules',
      image: '/images/8efe21916c10.webp',
      description: 'The ultimate modular solution offering maximum space and luxury.'
    }
  ];

  return (
    <section id="m-range" className="relative py-16 sm:py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-[#000000] via-[#0D1117] to-[#0A0A0A] w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-3 px-4 sm:px-6 py-2 sm:py-3 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-4 sm:mb-6">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-emerald-500 rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.5)]">
              <span className="text-base sm:text-lg font-bold text-black">M</span>
            </div>
            <span className="text-emerald-400 text-xs sm:text-sm uppercase tracking-widest">M SERIES</span>
          </div>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-thin mb-4 sm:mb-6 px-4 text-white">
            Modular <span className="text-emerald-400" style={{ textShadow: '0 0 30px rgba(16,185,129,0.6)' }}>Adventure Pods</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            Revolutionary flat-pack modular cabins designed for the world's most remote and challenging locations
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center mb-12 sm:mb-16 lg:mb-20 w-full">
          {/* Left: Features */}
          <div className="space-y-4 sm:space-y-6 order-2 lg:order-1 w-full">
            {features.map((feature, index) => (
              <div
                key={index}
                onMouseEnter={() => setActiveFeature(index)}
                onClick={() => setActiveFeature(index)}
                className={`p-6 sm:p-8 rounded-2xl border transition-all duration-300 cursor-pointer w-full ${
                  activeFeature === index
                    ? 'bg-emerald-500/10 border-emerald-400/50 shadow-[0_0_30px_rgba(16,185,129,0.2)]'
                    : 'bg-black/40 border-emerald-500/20 hover:border-emerald-500/40'
                }`}
              >
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                    activeFeature === index ? 'bg-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.5)]' : 'bg-emerald-500/20'
                  }`}>
                    <i className={`${feature.icon} text-xl sm:text-2xl ${activeFeature === index ? 'text-black' : 'text-emerald-400'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-medium mb-2 text-white">{feature.title}</h3>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Image */}
          <div className="relative order-1 lg:order-2 w-full">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-emerald-400/20 blur-3xl rounded-full"></div>
            <div className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px]">
              <img 
                src="/images/7889fed16b89.webp" 
                alt="M Range Modular Cabin"
                className="w-full h-full object-cover rounded-3xl shadow-[0_0_40px_rgba(16,185,129,0.3)]"
              />
            </div>
          </div>
        </div>

        {/* Models Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12 lg:mb-16 w-full">
          {models.map((model, index) => (
            <div key={index} className="group relative bg-black/40 backdrop-blur-xl border border-emerald-500/20 rounded-2xl overflow-hidden hover:border-emerald-400/50 hover:shadow-[0_0_30px_rgba(16,185,129,0.2)] transition-all duration-500 cursor-pointer w-full">
              <div className="relative w-full h-48 sm:h-56">
                <img 
                  src={model.image}
                  alt={model.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                <div className="absolute top-4 left-4 px-3 py-1 bg-emerald-500 text-black text-sm font-medium rounded-full shadow-[0_0_15px_rgba(16,185,129,0.5)]">
                  {model.name}
                </div>
                <div className="absolute top-4 right-4 px-3 py-1 bg-black/60 backdrop-blur-sm text-emerald-400 text-xs font-medium rounded-full">
                  {model.modules}
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <p className="text-sm text-gray-300 mb-4 leading-relaxed">{model.description}</p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Dimensions</span>
                    <span className="font-medium text-white">{model.dimensions}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Area</span>
                    <span className="font-medium text-white">{model.area}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Occupancy</span>
                    <span className="font-medium text-white">{model.occupancy}</span>
                  </div>
                </div>
                <Link to={`/contact?model=${encodeURIComponent(model.name)}`} className="block w-full px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm font-medium rounded-full hover:bg-emerald-500 hover:text-black transition-all whitespace-nowrap cursor-pointer text-center">
                  Request Quote
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center w-full flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/m-range-details"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-emerald-500 text-black text-base font-medium rounded-full hover:bg-emerald-400 transition-all hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] whitespace-nowrap cursor-pointer"
          >
            Explore M Series Details
            <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center group-hover:shadow-lg transition-all">
              <i className="ri-arrow-right-line text-emerald-400"></i>
            </div>
          </Link>
          <Link to="/contact?model=M%20Series" className="group inline-flex items-center gap-3 px-8 py-4 bg-emerald-600 text-white text-base font-medium rounded-full hover:bg-emerald-500 transition-all hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] whitespace-nowrap cursor-pointer">
            Request Quote for M Series
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center group-hover:shadow-lg transition-all">
              <i className="ri-mail-send-line text-emerald-600"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
